import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ThemeContext } from "../context/ThemeContext";

/**
 * Sun → moon morph theme toggle, adapted from the v0 template:
 *   - TypeScript → JSX (drop annotations)
 *   - motion/react → framer-motion (already installed at v12)
 *   - Document-class toggle → ThemeContext.toggleTheme() so the rest of
 *     the app's ThemeContext consumers (Settings, AppearanceTab, etc.)
 *     stay in sync
 *   - <style> injection swapped for Tailwind `dark:` classes
 *
 * Click sound is opt-in via `sound` prop. Defaults off for the dashboard
 * navbar — the click feedback adds noise where the user expects silence.
 */

let _ctx = null;
let _buf = null;

const audioCtx = () => {
  if (!_ctx) {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
};

const ensureBuf = (ac) => {
  if (_buf && _buf.sampleRate === ac.sampleRate) return _buf;
  const rate = ac.sampleRate;
  const len = Math.floor(rate * 0.006);
  const buf = ac.createBuffer(1, len, rate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / len;
    const sine = Math.sin(2 * Math.PI * 3400 * t);
    const noise = Math.random() * 2 - 1;
    ch[i] = (sine * 0.6 + noise * 0.4) * (1 - t) ** 3;
  }
  _buf = buf;
  return buf;
};

const tick = (last) => {
  const now = performance.now();
  if (now - last.current < 80) return;
  last.current = now;
  try {
    const ac = audioCtx();
    const buf = ensureBuf(ac);
    const src = ac.createBufferSource();
    const gain = ac.createGain();
    src.buffer = buf;
    gain.gain.value = 0.08;
    src.connect(gain);
    gain.connect(ac.destination);
    src.start();
  } catch {
    /* silent */
  }
};

const AnimatedThemeToggler = ({ sound = false, className = "" }) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  // For the initial render the parent ThemeContext is the source of truth.
  // We mirror that into local state so the SVG animations can read it
  // synchronously without re-rendering the whole subtree on every theme tick.
  const [isDark, setIsDark] = useState(theme === "dark");
  const rawId = useId();
  const maskId = `att${rawId.replace(/:/g, "")}`;
  const lastSnd = useRef(0);
  const isFirst = useRef(true);

  useEffect(() => {
    setIsDark(theme === "dark");
  }, [theme]);

  useEffect(() => {
    requestAnimationFrame(() => {
      isFirst.current = false;
    });
  }, []);

  const handleClick = () => {
    toggleTheme();
    if (sound) tick(lastSnd);
  };

  const spring = isFirst.current
    ? { duration: 0 }
    : { type: "spring", stiffness: 380, damping: 30 };

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.86 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      className={`p-1.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors outline-none ${className}`}
    >
      <motion.svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        initial={false}
        animate={{ rotate: isDark ? 270 : 0 }}
        transition={spring}
        style={{ overflow: "visible" }}
      >
        {/* Mask carves the crescent from the center circle when dark. */}
        <mask id={maskId}>
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <motion.circle
            initial={false}
            animate={{ cx: isDark ? 17 : 33, cy: isDark ? 8 : 0 }}
            transition={spring}
            r="9"
            fill="black"
          />
        </mask>

        {/* Center body — small sun circle, or large masked moon body. */}
        <motion.circle
          cx="12"
          cy="12"
          fill="currentColor"
          stroke="none"
          mask={`url(#${maskId})`}
          initial={false}
          animate={{ r: isDark ? 9 : 5 }}
          transition={spring}
        />

        {/* Rays — shrink and rotate away in dark mode. */}
        <motion.g
          initial={false}
          animate={{
            opacity: isDark ? 0 : 1,
            scale: isDark ? 0 : 1,
            rotate: isDark ? -30 : 0,
          }}
          transition={spring}
          style={{ transformOrigin: "12px 12px" }}
        >
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="5.64" y1="5.64" x2="4.22" y2="4.22" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          <line x1="5.64" y1="18.36" x2="4.22" y2="19.78" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        </motion.g>
      </motion.svg>
    </motion.button>
  );
};

export default AnimatedThemeToggler;
